import { lazy, Suspense, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useConcreteAccess } from '@/lib/concrete';
import { useBranches } from '@/pages/concrete/useBranches';
import { PLANT, usePlantAccess } from '@/lib/plant';
import type { PlantOperation } from '@/types/models';
import { useLookups, useRows } from '@/hooks/useLive';
import { save, softDelete, upsert } from '@/lib/crud';
import { date as fmtDate, humanise, num } from '@/lib/format';
import { useAuthStore } from '@/stores/authStore';
import { roleLock } from '@/lib/editability';
import { Panel } from '@/components/ui/Panel';
import { Button } from '@/components/ui/Button';
import { ConfirmRemove } from '@/components/ui/ConfirmRemove';
import { Drawer } from '@/components/ui/Drawer';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { StatusChip } from '@/components/ui/StatusChip';
import { NumberInput, SelectInput, TextArea, TextInput } from '@/components/ui/Field';
import { atQuarry } from '@/lib/fleet';

interface Target extends Record<string, unknown> {
  id: string;
  period_type: string;
  period_start: string;
  period_end: string;
  measure: string;
  scope: string;
  machine_id: string | null;
  machine_type_id: string | null;
  product_id: string | null;
  target_value: number;
  notes: string | null;
}

const PERIODS = ['day', 'week', 'month', 'quarter', 'year'].map((v) => ({ value: v, label: humanise(v) }));
const MEASURES = [
  { value: 'tonnes', label: 'Tonnes' },
  { value: 'loads', label: 'Loads' },
  { value: 'm3', label: 'Cubic metres' },
  { value: 'machine_hours', label: 'Machine hours' },
];
const SCOPES = [
  { value: 'quarry', label: 'Whole quarry' },
  { value: 'machine', label: 'A single machine' },
  { value: 'machine_type', label: 'A machine type' },
  { value: 'product', label: 'A product' },
];

const UNIT: Record<string, string> = { tonnes: 't', loads: 'loads', m3: 'm³', machine_hours: 'h' };

// Each module's own targets screen, shown here as one of the tabs (0069).
const ConcreteTargets = lazy(() => import('@/pages/concrete/ConcreteTargets').then((m) => ({ default: m.ConcreteTargets })));
const PlantTargets = lazy(() => import('@/pages/plant/PlantDashboard').then((m) => ({ default: m.PlantTargets })));

type ModuleKey = 'quarry' | 'concrete' | PlantOperation;

/**
 * Production targets for every operation in one place (0069): the quarry's
 * tonnes, concrete m³ per branch, and Brick Plant / Pre-cast pieces per
 * site. Each module keeps its own Targets screen too; both edit the same
 * records. A tab shows only where the user may set targets.
 */
export function Targets() {
  const [params, setParams] = useSearchParams();
  const concrete = useConcreteAccess();
  const concreteBranches = useBranches();
  const brick = usePlantAccess('brick');
  const precast = usePlantAccess('precast');
  const tabs: { key: ModuleKey; label: string; show: boolean }[] = [
    { key: 'quarry', label: 'Quarry · tonnes', show: true },
    { key: 'concrete', label: 'Concrete · m³ per branch', show: concreteBranches.some((b) => concrete.canLead(b.id)) },
    { key: 'brick', label: `${PLANT.brick.label} · pieces`, show: brick.sites.some((s) => brick.canLead(s.id)) },
    { key: 'precast', label: `${PLANT.precast.label} · pieces`, show: precast.sites.some((s) => precast.canLead(s.id)) },
  ];
  const shown = tabs.filter((t) => t.show);
  const want = params.get('module') as ModuleKey | null;
  const active = shown.find((t) => t.key === want)?.key ?? 'quarry';
  const spinner = <div className="centered"><span className="spinner" /></div>;
  return (
    <div className="col" style={{ gap: 'var(--sp-3)' }}>
      {shown.length > 1 && (
        <div className="row targets-tabs" style={{ gap: 'var(--sp-2)', flexWrap: 'wrap' }} role="tablist">
          {shown.map((t) => (
            <Button key={t.key} role="tab" aria-selected={t.key === active}
              variant={t.key === active ? 'primary' : 'secondary'} style={{ minHeight: 32 }}
              onClick={() => setParams(t.key === 'quarry' ? {} : { module: t.key }, { replace: true })}>
              {t.label}
            </Button>
          ))}
        </div>
      )}
      {active === 'quarry' && <QuarryTargets />}
      {active === 'concrete' && <Suspense fallback={spinner}><ConcreteTargets /></Suspense>}
      {(active === 'brick' || active === 'precast') && (
        <Suspense fallback={spinner}><PlantTargets key={active} op={active} /></Suspense>
      )}
    </div>
  );
}

function QuarryTargets() {
  const targets = useRows<Target>('production_targets', 'period_start');
  const machines = useRows<Record<string, unknown>>('machines', 'fleet_no');
  const types = useRows<Record<string, unknown>>('machine_types', 'sort_order');
  const products = useRows<Record<string, unknown>>('products', 'code');
  const lookups = useLookups([
    { table: 'machines', label: 'fleet_no' },
    { table: 'machine_types', label: 'name' },
    { table: 'products', label: 'name' },
  ]);
  const canEdit = useAuthStore((s) => s.hasRole('manager', 'admin'));
  const role = useAuthStore((s) => s.profile?.role);
  // Everyone may open a record; who may change it is said inside (0046).
  const lock = roleLock(role, ['manager', 'admin'], 'production targets');

  const [editing, setEditing] = useState<Target | Partial<Target> | null>(null);
  const [v, setV] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!editing) return;
    const thisYear = new Date().getFullYear();
    setV({
      period_type: 'day',
      measure: 'tonnes',
      scope: 'quarry',
      period_start: `${thisYear}-01-01`,
      period_end: `${thisYear}-12-31`,
      ...editing,
    });
    setErrors({});
  }, [editing]);

  const set = (k: string, val: unknown) => setV((d) => ({ ...d, [k]: val }));

  function scopeLabel(t: Target): string {
    if (t.scope === 'machine') return lookups.machines?.get(t.machine_id ?? '') ?? 'Machine';
    if (t.scope === 'machine_type') return lookups.machine_types?.get(t.machine_type_id ?? '') ?? 'Machine type';
    if (t.scope === 'product') return lookups.products?.get(t.product_id ?? '') ?? 'Product';
    return 'Whole quarry';
  }

  async function submit() {
    const e: Record<string, string> = {};
    if (!v.period_start) e.period_start = 'Required';
    if (!v.period_end) e.period_end = 'Required';
    if (v.period_start && v.period_end && String(v.period_end) < String(v.period_start)) {
      e.period_end = 'End is before the start';
    }
    if (v.target_value == null || Number(v.target_value) < 0) e.target_value = 'Enter a target';
    if (v.scope === 'machine' && !v.machine_id) e.machine_id = 'Choose a machine';
    if (v.scope === 'machine_type' && !v.machine_type_id) e.machine_type_id = 'Choose a machine type';
    if (v.scope === 'product' && !v.product_id) e.product_id = 'Choose a product';
    setErrors(e);
    if (Object.keys(e).length) return;

    setBusy(true);
    // The database rejects a scope that carries the wrong reference, so
    // clear the ones that do not apply rather than sending stale ids.
    const payload = {
      ...v,
      machine_id: v.scope === 'machine' ? v.machine_id : null,
      machine_type_id: v.scope === 'machine_type' ? v.machine_type_id : null,
      product_id: v.scope === 'product' ? v.product_id : null,
    };
    const ok = await save(
      () => upsert('production_targets', payload as Record<string, unknown> & { id?: string }),
      'Target saved',
    );
    setBusy(false);
    if (ok) setEditing(null);
  }

  const columns: Column<Target>[] = [
    { key: 'period_type', label: 'Period', render: (t) => humanise(t.period_type) },
    { key: 'period_start', label: 'From', render: (t) => fmtDate(t.period_start) },
    { key: 'period_end', label: 'To', render: (t) => fmtDate(t.period_end) },
    { key: 'scope', label: 'Applies to', value: (t) => scopeLabel(t), render: (t) => scopeLabel(t) },
    { key: 'measure', label: 'Measure', render: (t) => <StatusChip tone="blue" dot={false}>{humanise(t.measure)}</StatusChip> },
    {
      key: 'target_value', label: 'Target', align: 'right',
      render: (t) => (
        <span className="tabular">
          <strong>{num(t.target_value, 0)}</strong> <span className="muted">{UNIT[t.measure] ?? ''}</span>
        </span>
      ),
    },
    { key: 'notes', label: 'Notes' },
  ];

  return (
    <>
      <Panel
        title="Production targets"
        flush
        actions={
          canEdit
            ? <Button style={{ minHeight: 32 }} onClick={() => setEditing({})}>+ New target</Button>
            : <span className="small muted">Read only for your role</span>
        }
      >
        <div style={{ padding: 'var(--sp-3) var(--sp-3) 0' }} className="small muted">
          The quarry's tonnes. A target applies over a date range, so it can be revised
          mid-year without destroying the comparison for months already closed. The
          dashboard measures the month against the month target (or the daily target
          for the day). Concrete, Brick Plant and Pre-cast targets are on their tabs above.
        </div>
        <DataTable
          rows={targets}
          columns={columns}
          rowKey={(t) => t.id}
          onRowClick={(t) => setEditing(t)}
          defaultSort="period_start"
          searchPlaceholder="Search targets…"
          empty="No targets set. Without one, the dashboard cannot show achievement."
        />
      </Panel>

      <Drawer
        open={editing !== null}
        title={editing && 'id' in editing ? 'Edit target' : 'New target'}
        subtitle="Actual against target drives the achievement figure on the dashboard"
        locked={lock}
        onClose={() => setEditing(null)}
        footer={
          <>
            {editing && 'id' in editing && (
              <ConfirmRemove
              disabled={busy}
              label="Remove"
              question="Remove this target?"
              onConfirm={async () => {
                const ok = await save(() => softDelete('production_targets', String(editing.id)), 'Target removed');
                // Close only if it went through. A refusal leaves the record
                // open with the reason on screen, rather than closing over it.
                if (ok !== null) setEditing(null);
                return ok !== null;
              }}
            />
            )}
            <span className="spacer" />
            <Button variant="secondary" onClick={() => setEditing(null)} disabled={busy}>Cancel</Button>
            <Button onClick={submit} loading={busy}>Save</Button>
          </>
        }
      >
        <div className="form-grid">
          <SelectInput id="t-period" label="Period" required value={(v.period_type as string) ?? 'day'}
            options={PERIODS} onChange={(x) => set('period_type', x)}
            help="A daily target is compared against each day in the range." />
          <SelectInput id="t-measure" label="Measure" required value={(v.measure as string) ?? 'tonnes'}
            options={MEASURES} onChange={(x) => set('measure', x)} />

          <TextInput id="t-from" label="Effective from" type="date" required error={errors.period_start}
            value={String(v.period_start ?? '')} onChange={(x) => set('period_start', x)} />
          <TextInput id="t-to" label="Effective to" type="date" required error={errors.period_end}
            value={String(v.period_end ?? '')} onChange={(x) => set('period_end', x)} />

          <SelectInput id="t-scope" label="Applies to" required value={(v.scope as string) ?? 'quarry'}
            options={SCOPES} onChange={(x) => set('scope', x)} />
          <NumberInput id="t-value" label="Target" required min={0} step={10}
            suffix={UNIT[String(v.measure ?? 'tonnes')]} error={errors.target_value}
            value={(v.target_value as number) ?? null} onChange={(x) => set('target_value', x)} />

          {v.scope === 'machine' && (
            <SelectInput id="t-machine" label="Machine" required error={errors.machine_id}
              value={(v.machine_id as string) ?? null}
              options={(machines ?? []).filter((m) => atQuarry(m as { site_id?: string }))
                .map((m) => ({ value: String(m.id), label: String(m.fleet_no) }))}
              onChange={(x) => set('machine_id', x)} />
          )}
          {v.scope === 'machine_type' && (
            <SelectInput id="t-type" label="Machine type" required error={errors.machine_type_id}
              value={(v.machine_type_id as string) ?? null}
              options={(types ?? []).map((t) => ({ value: String(t.id), label: String(t.name) }))}
              onChange={(x) => set('machine_type_id', x)} />
          )}
          {v.scope === 'product' && (
            <SelectInput id="t-product" label="Product" required error={errors.product_id}
              value={(v.product_id as string) ?? null}
              options={(products ?? []).map((p) => ({ value: String(p.id), label: String(p.name) }))}
              onChange={(x) => set('product_id', x)} />
          )}

          <div className="field--full">
            <TextArea id="t-notes" label="Notes" value={String(v.notes ?? '')}
              onChange={(x) => set('notes', x || null)} />
          </div>
        </div>
      </Drawer>
    </>
  );
}
